import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import BuySection from '../components/BuySection';

/* ── Lo Shu grid position map ── */
const GRID_POSITIONS = {
    4: [0, 0], 9: [0, 1], 2: [0, 2],
    3: [1, 0], 5: [1, 1], 7: [1, 2],
    8: [2, 0], 1: [2, 1], 6: [2, 2],
};

const DEMO_PROFILES = [
    { label: 'DOB: 15 Aug 1990', digits: [1, 5, 8, 1, 9, 9, 0], mulank: 6, bhagyank: 4 },
    { label: 'DOB: 22 Mar 1985', digits: [2, 2, 3, 1, 9, 8, 5], mulank: 4, bhagyank: 3 },
    { label: 'DOB: 07 Nov 2001', digits: [7, 1, 1, 2, 0, 0, 1], mulank: 7, bhagyank: 3 },
];

function buildGrid(digits) {
    const g = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0 };
    digits.forEach(d => { if (d >= 1 && d <= 9) g[d]++; });
    return g;
}

function MiniLoshuGrid({ digits, mulank, bhagyank, animated }) {
    const grid = buildGrid(digits);
    const cells = Array(3).fill(null).map(() => Array(3).fill(null));
    Object.entries(GRID_POSITIONS).forEach(([num, [r, c]]) => {
        cells[r][c] = { num: parseInt(num), count: grid[num] ?? 0 };
    });

    return (
        <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 4, borderRadius: 12, overflow: 'hidden',
            border: '2px solid rgba(206,173,128,0.3)',
            boxShadow: '0 0 40px rgba(206,173,128,0.12)',
        }}>
            {cells.flat().map((cell, i) => {
                const { num, count } = cell;
                const present = count > 0;
                const isM = mulank === num;
                const isB = bhagyank === num;
                const delay = animated ? `${i * 0.07}s` : '0s';
                return (
                    <div key={i} style={{
                        background: present
                            ? 'linear-gradient(135deg, rgba(206,173,128,0.18), rgba(206,173,128,0.06))'
                            : 'rgba(15,23,42,0.8)',
                        border: '1px solid rgba(206,173,128,0.15)',
                        padding: '12px 8px',
                        display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center',
                        minHeight: 72, position: 'relative',
                        animation: animated ? `fadeInUp 0.4s ease ${delay} both` : 'none',
                        transition: 'all 0.3s ease',
                    }}>
                        {(isM || isB) && (
                            <div style={{
                                position: 'absolute', top: 3, right: 4,
                                fontSize: 7, fontWeight: 800, color: '#cead80',
                                letterSpacing: 0.3,
                            }}>
                                {isM ? '◆M' : '★B'}
                            </div>
                        )}
                        <div style={{
                            fontSize: 22, fontWeight: 900,
                            color: present ? '#cead80' : 'rgba(206,173,128,0.2)',
                            lineHeight: 1, marginBottom: 4,
                            textShadow: present ? '0 0 12px rgba(206,173,128,0.5)' : 'none',
                        }}>{num}</div>
                        {present && (
                            <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                                {Array.from({ length: Math.min(count, 4) }).map((_, di) => (
                                    <div key={di} style={{
                                        width: 5, height: 5, borderRadius: '50%',
                                        background: isM ? '#4f6ef7' : isB ? '#F59E0B' : '#cead80',
                                    }} />
                                ))}
                            </div>
                        )}
                        {!present && (
                            <div style={{ fontSize: 8, color: 'rgba(206,173,128,0.2)', letterSpacing: 0.5 }}>—</div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

const FEATURES = [
    {
        icon: '🔢', title: 'Lo Shu Grid',
        desc: 'Ancient 3×3 Chinese numerological grid. Instantly maps all 9 energy planes from any date of birth.',
        color: '#cead80',
    },
    {
        icon: '🪐', title: 'Vedic Planetary Grid',
        desc: 'Navagraha planetary mapping — see which cosmic forces drive your client\'s destiny.',
        color: '#818cf8',
    },
    {
        icon: '🌟', title: 'Mulank & Bhagyank',
        desc: 'Automatically calculates Root Number and Fortune Number with deep interpretations.',
        color: '#F59E0B',
    },
    {
        icon: '⚡', title: 'Rajyog & Golden Yog',
        desc: 'Detects all 8 Rajyog combinations and 8 Golden Yog patterns formed in the birth chart.',
        color: '#34d399',
    },
    {
        icon: '💊', title: 'Missing Number Remedies',
        desc: 'Personalised planet-based remedies for every missing number — ready to share with clients.',
        color: '#f472b6',
    },
    {
        icon: '📅', title: 'Vimshottari Dasha Chart',
        desc: '120-year Vedic planetary timeline. Know exactly which planet governs each life phase.',
        color: '#0ea5e9',
    },
    {
        icon: '🎨', title: 'Lucky Numbers & Colours',
        desc: 'Instant personalised lucky & enemy numbers with colour recommendations.',
        color: '#a78bfa',
    },
    {
        icon: '🌐', title: 'Multi-language Reports',
        desc: 'Generate and share reports in 18+ languages including Hindi, Marathi, Gujarati, Tamil and more.',
        color: '#fb923c',
    },
];

const TESTIMONIALS = [
    {
        name: 'Priya Sharma', role: 'Numerology Consultant, Pune',
        text: 'I was spending 3 hours manually drawing grids for each client. Now I generate a full professional report in under 2 minutes. My client base tripled.',
        avatar: 'PS', stars: 5,
    },
    {
        name: 'Rajan Mehta', role: 'Astrologer & Numerologist, Mumbai',
        text: 'The Rajyog detection alone is worth everything. It catches patterns I would miss. My clients are blown away by the depth of analysis.',
        avatar: 'RM', stars: 5,
    },
    {
        name: 'Riya Joshi', role: 'Life Coach & Healer, Delhi',
        text: 'The missing number remedies are so detailed and practical. My clients actually follow them and come back for more sessions. Incredible tool.',
        avatar: 'AJ', stars: 5,
    },
];

const LIFETIME_FEATURES = [
    'Lo Shu Grid with all 9 energy planes',
    'Rajyog & Golden Yog auto-detection (16 combos)',
    'Vedic Navagraha Planetary Grid',
    'Mulank & Bhagyank calculations',
    'Personalised Missing Number Remedies',
    'Lucky & Enemy Numbers with Colour Palettes',
    'Vimshottari Dasha Chart — 120-year timeline',
    'Reports in 18+ languages via WhatsApp / PDF',
    'Lifetime access — no recurring fees',
];

const FAQS = [
    {
        q: 'Do I need any numerology experience to use this?',
        a: 'No. The software handles all calculations automatically. You just enter the client\'s name, DOB, and mobile number — and get a complete, interpretation-ready report instantly.',
    },
    {
        q: 'Can I share reports directly with my clients?',
        a: 'Yes. Reports can be exported as PDF, shared via WhatsApp, or sent directly by email — all from within the dashboard.',
    },
    {
        q: 'Is my client data secure?',
        a: 'Absolutely. All data is encrypted at rest and in transit. We follow GDPR best practices. You own your data and can export or delete it at any time.',
    },
    {
        q: 'What is the refund policy?',
        a: 'This is a one-time lifetime purchase. If you are not satisfied within 7 days of purchase, we refund 100% — no questions asked.',
    },
    {
        q: 'Can I use this for teaching numerology?',
        a: 'Yes. Many of our users are numerology teachers who generate demo charts for classes. The Enterprise plan includes team accounts perfect for institutions.',
    },
];

export default function Home() {
    const { user, isSubscribed } = useAuth();
    const canvasRef = useRef(null);
    const [demoIdx, setDemoIdx] = useState(0);
    const [animKey, setAnimKey] = useState(0);
    const [openFaq, setOpenFaq] = useState(null);

    // Starfield
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const resize = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        resize();
        window.addEventListener('resize', resize);
        const stars = Array.from({ length: 180 }, () => ({
            x: Math.random() * canvas.width,
            y: Math.random() * canvas.height,
            r: Math.random() * 1.6,
            a: Math.random(),
            speed: Math.random() * 0.012 + 0.004,
        }));
        let animId;
        const draw = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            stars.forEach(s => {
                s.a += s.speed;
                ctx.beginPath();
                ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(180,150,100,${Math.abs(Math.sin(s.a)) * 0.6})`;
                ctx.fill();
            });
            animId = requestAnimationFrame(draw);
        };
        draw();
        return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', resize); };
    }, []);

    // Auto-rotate demo
    useEffect(() => {
        const t = setInterval(() => {
            setDemoIdx(i => (i + 1) % DEMO_PROFILES.length);
            setAnimKey(k => k + 1);
        }, 3500);
        return () => clearInterval(t);
    }, []);

    const demo = DEMO_PROFILES[demoIdx];

    return (
        <div className="cosmic-bg" style={{ minHeight: '100vh', position: 'relative', overflow: 'hidden' }}>
            <canvas ref={canvasRef} style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0 }} />

            {/* ════════════════ HERO ════════════════ */}
            <section style={{ position: 'relative', zIndex: 1, padding: '130px 24px 60px', textAlign: 'center' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
                    <span style={{
                        background: 'linear-gradient(135deg, rgba(206,173,128,0.2), rgba(206,173,128,0.06))',
                        border: '1px solid rgba(206,173,128,0.4)',
                        borderRadius: 50, padding: '6px 20px',
                        fontSize: 12, fontWeight: 700, color: '#cead80',
                        letterSpacing: 1.2, textTransform: 'uppercase',
                    }}>
                        ✦ Professional Numerology Software
                    </span>
                </div>

                <h1 style={{
                    fontSize: 'clamp(38px, 6.5vw, 80px)', fontWeight: 900,
                    lineHeight: 1.08, marginBottom: 24,
                    fontFamily: 'Plus Jakarta Sans, sans-serif',
                }}>
                    <span style={{
                        background: 'linear-gradient(135deg, #e8d4a8, #cead80, #a07e4a)',
                        WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                    }}>Loshu Grid Calculator</span>
                    <br />
                    <span style={{ color: '#000000ff', fontSize: '0.7em' }}>for Numerology Professionals</span>
                </h1>

                <p style={{
                    fontSize: 18, color: '#94a3b8', maxWidth: 620,
                    margin: '0 auto 40px', lineHeight: 1.85,
                }}>
                    Generate <strong style={{ color: '#cead80' }}>complete numerology reports</strong> in seconds — Lo Shu Grid, Vedic Grid, Rajyog, Dasha Chart, Missing Remedies and more.
                    Built for consultants who want to <strong style={{ color: '#cead80' }}>save time and impress clients.</strong>
                </p>

                <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 60 }}>
                    {user ? (
                        <Link to="/dashboard" style={{
                            textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 10,
                            background: 'linear-gradient(135deg, #a07e4a, #cead80)', color: '#1a0a00',
                            padding: '16px 36px', borderRadius: 50, fontSize: 16, fontWeight: 800,
                            boxShadow: '0 8px 32px rgba(206,173,128,0.35)',
                            transition: 'all 0.3s ease',
                        }}>Open Dashboard</Link>
                    ) : (
                        <>
                            {/* <Link to="/register" style={{
                                textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 10,
                                background: 'linear-gradient(135deg, #a07e4a, #cead80)', color: '#1a0a00',
                                padding: '16px 36px', borderRadius: 50, fontSize: 16, fontWeight: 800,
                                boxShadow: '0 8px 32px rgba(206,173,128,0.35)',
                                animation: 'pulse-glow 2s ease-in-out infinite',
                            }}>Start Free Trial</Link> */}
                            <Link to="/login" style={{
                                textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 10,
                                background: 'transparent', color: '#cead80',
                                border: '1.5px solid rgba(206,173,128,0.45)',
                                padding: '16px 36px', borderRadius: 50, fontSize: 16, fontWeight: 700,
                            }}>Sign In →</Link>
                        </>
                    )}
                </div>

                {/* Stats row */}
                <div className="home-stats-row" style={{
                    display: 'flex', gap: 0, justifyContent: 'center',
                    flexWrap: 'wrap', marginBottom: 0,
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(206,173,128,0.15)',
                    borderRadius: 20, padding: '20px 0',
                    maxWidth: 700, margin: '0 auto',
                }}>
                    {[
                        ['1.5L+', 'Reports Generated'],
                        ['8', 'Numerology Tools'],
                        ['18+', 'Languages'],
                        ['100%', 'Vedic Accurate'],
                    ].map(([n, l], i) => (
                        <div key={l} style={{
                            textAlign: 'center', flex: 1, minWidth: 120,
                            borderRight: i < 3 ? '1px solid rgba(206,173,128,0.1)' : 'none',
                            padding: '8px 16px',
                        }}>
                            <div style={{
                                fontSize: 28, fontWeight: 900, marginBottom: 2,
                                background: 'linear-gradient(135deg, #a07e4a, #cead80)',
                                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                            }}>{n}</div>
                            <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.8 }}>{l}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* ════════════════ LIVE DEMO ════════════════ */}
            <section style={{ position: 'relative', zIndex: 1, padding: '60px 24px 80px' }}>
                <div style={{ maxWidth: 1100, margin: '0 auto' }}>
                    <div style={{ textAlign: 'center', marginBottom: 48 }}>
                        <span style={{
                            display: 'inline-block', marginBottom: 12,
                            background: 'rgba(79,110,247,0.12)', border: '1px solid rgba(79,110,247,0.25)',
                            borderRadius: 50, padding: '5px 18px',
                            fontSize: 11, fontWeight: 700, color: '#7b96fa',
                            letterSpacing: 1, textTransform: 'uppercase',
                        }}>⚡ Live Demo</span>
                        <h2 style={{
                            fontSize: 'clamp(26px, 4vw, 44px)', fontWeight: 900, marginBottom: 12,
                            color: '#000000ff', fontFamily: 'Plus Jakarta Sans, sans-serif',
                        }}>See the Grid Come Alive</h2>
                        <p style={{ color: '#64748b', fontSize: 16 }}>
                            Watch a real Lo Shu Grid generated automatically from different dates of birth
                        </p>
                    </div>

                    <div className="home-demo-grid" style={{
                        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                        gap: 40, alignItems: 'center',
                    }}>
                        {/* Grid preview */}
                        <div style={{
                            background: 'linear-gradient(135deg, rgba(206,173,128,0.08), rgba(206,173,128,0.02))',
                            border: '1.5px solid rgba(206,173,128,0.2)',
                            borderRadius: 24, padding: 32,
                            boxShadow: '0 20px 60px rgba(0,0,0,0.4), 0 0 0 1px rgba(206,173,128,0.08)',
                        }}>
                            <div style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                marginBottom: 20,
                            }}>
                                <div>
                                    <div style={{ fontSize: 12, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 }}>
                                        Auto-rotating demo
                                    </div>
                                    <div style={{ fontSize: 15, fontWeight: 700, color: '#cead80' }}>{demo.label}</div>
                                </div>
                                <div style={{ display: 'flex', gap: 6 }}>
                                    {DEMO_PROFILES.map((_, i) => (
                                        <button key={i} onClick={() => { setDemoIdx(i); setAnimKey(k => k + 1); }} style={{
                                            width: 8, height: 8, borderRadius: '50%',
                                            background: i === demoIdx ? '#cead80' : 'rgba(206,173,128,0.25)',
                                            border: 'none', cursor: 'pointer', padding: 0,
                                            transition: 'all 0.3s',
                                        }} />
                                    ))}
                                </div>
                            </div>
                            <MiniLoshuGrid key={animKey} digits={demo.digits} mulank={demo.mulank} bhagyank={demo.bhagyank} animated />

                            {/* Legend */}
                            <div style={{ display: 'flex', gap: 16, marginTop: 18, justifyContent: 'center', flexWrap: 'wrap' }}>
                                {[
                                    { dot: '#4f6ef7', label: '◆ Mulank' },
                                    { dot: '#F59E0B', label: '★ Bhagyank' },
                                    { dot: '#cead80', label: 'Present' },
                                    { dot: 'rgba(206,173,128,0.15)', label: 'Missing' },
                                ].map(({ dot, label }) => (
                                    <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#64748b' }}>
                                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: dot }} />
                                        {label}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* What you get */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                            <h3 style={{ fontSize: 22, fontWeight: 800, color: '#000000ff', marginBottom: 4 }}>
                                A full report includes:
                            </h3>
                            {[
                                { icon: '🔢', text: 'Lo Shu Grid with all 9 energy planes annotated' },
                                { icon: '⚡', text: 'Rajyog & Golden Yog combinations detected automatically' },
                                { icon: '🪐', text: 'Vedic Planetary Grid (Navagraha mapping)' },
                                { icon: '🔮', text: 'Mulank, Bhagyank & DOB interpretations' },
                                { icon: '💊', text: 'Planet-specific remedies for every missing number' },
                                { icon: '🎯', text: 'Lucky & enemy numbers with colour palettes' },
                                { icon: '📅', text: 'Vimshottari Dasha Chart — 120-year timeline' },
                                { icon: '🌐', text: 'Shareable in 18+ languages via WhatsApp / PDF' },
                            ].map(({ icon, text }) => (
                                <div key={text} style={{
                                    display: 'flex', alignItems: 'flex-start', gap: 14,
                                    padding: '12px 16px', borderRadius: 12,
                                    background: 'rgba(255,255,255,0.03)',
                                    border: '1px solid rgba(206,173,128,0.1)',
                                    transition: 'all 0.25s ease',
                                }}>
                                    <span style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>{icon}</span>
                                    <span style={{ fontSize: 14, color: '#94a3b8', lineHeight: 1.6 }}>{text}</span>
                                </div>
                            ))}
                            <Link to={user ? '/dashboard' : '/register'} style={{
                                textDecoration: 'none', marginTop: 8,
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                                background: 'linear-gradient(135deg, #a07e4a, #cead80)', color: '#1a0a00',
                                padding: '14px 32px', borderRadius: 50, fontSize: 15, fontWeight: 800,
                                boxShadow: '0 6px 24px rgba(206,173,128,0.3)',
                            }}>
                                {user ? 'Go to Dashboard →' : 'Create Account →'}
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* ════════════════ FEATURES GRID ════════════════ */}
            <section style={{ position: 'relative', zIndex: 1, padding: '60px 24px 80px' }}>
                <div style={{ maxWidth: 1100, margin: '0 auto' }}>
                    <div style={{ textAlign: 'center', marginBottom: 56 }}>
                        <span style={{
                            display: 'inline-block', marginBottom: 12,
                            background: 'rgba(206,173,128,0.1)', border: '1px solid rgba(206,173,128,0.25)',
                            borderRadius: 50, padding: '5px 18px',
                            fontSize: 11, fontWeight: 700, color: '#cead80',
                            letterSpacing: 1, textTransform: 'uppercase',
                        }}>Everything Included</span>
                        <h2 style={{
                            fontSize: 'clamp(26px, 4vw, 44px)', fontWeight: 900, marginBottom: 12,
                            color: '#000000ff', fontFamily: 'Plus Jakarta Sans, sans-serif',
                        }}>8 Powerful Tools in One Platform</h2>
                        <p style={{ color: '#64748b', fontSize: 16, maxWidth: 560, margin: '0 auto' }}>
                            Everything a professional numerologist needs — from grid generation to client-ready remedies
                        </p>
                    </div>

                    <div className="home-features-grid" style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                        gap: 20,
                    }}>
                        {FEATURES.map((f, i) => (
                            <div key={i} style={{
                                padding: '24px 22px',
                                background: 'linear-gradient(145deg, rgba(255,255,255,0.04), rgba(255,255,255,0.01))',
                                border: `1.5px solid ${f.color}22`,
                                borderRadius: 18,
                                transition: 'all 0.3s ease',
                                cursor: 'default',
                                position: 'relative', overflow: 'hidden',
                                animation: `fadeInUp 0.5s ease ${i * 0.05}s both`,
                            }}
                                onMouseEnter={e => {
                                    e.currentTarget.style.border = `1.5px solid ${f.color}55`;
                                    e.currentTarget.style.boxShadow = `0 12px 40px ${f.color}18`;
                                    e.currentTarget.style.transform = 'translateY(-4px)';
                                }}
                                onMouseLeave={e => {
                                    e.currentTarget.style.border = `1.5px solid ${f.color}22`;
                                    e.currentTarget.style.boxShadow = 'none';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                }}
                            >
                                <div style={{
                                    width: 48, height: 48, borderRadius: 14, marginBottom: 16,
                                    background: `linear-gradient(135deg, ${f.color}22, ${f.color}0a)`,
                                    border: `1.5px solid ${f.color}33`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 22,
                                }}>
                                    {f.icon}
                                </div>
                                <h3 style={{
                                    fontSize: 16, fontWeight: 800, marginBottom: 8,
                                    color: f.color,
                                }}>{f.title}</h3>
                                <p style={{ fontSize: 13, color: '#64748b', lineHeight: 1.75 }}>{f.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ════════════════ HOW IT WORKS ════════════════ */}
            <section style={{ position: 'relative', zIndex: 1, padding: '60px 24px 80px' }}>
                <div style={{ maxWidth: 900, margin: '0 auto', textAlign: 'center' }}>
                    <span style={{
                        display: 'inline-block', marginBottom: 12,
                        background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.25)',
                        borderRadius: 50, padding: '5px 18px',
                        fontSize: 11, fontWeight: 700, color: '#34d399',
                        letterSpacing: 1, textTransform: 'uppercase',
                    }}> Quick Start</span>
                    <h2 style={{
                        fontSize: 'clamp(26px, 4vw, 44px)', fontWeight: 900, marginBottom: 16,
                        color: '#000000ff', fontFamily: 'Plus Jakarta Sans, sans-serif',
                    }}>Generate Your First Report in 60 Seconds</h2>
                    <p style={{ color: '#64748b', fontSize: 16, marginBottom: 60, maxWidth: 520, margin: '0 auto 60px' }}>
                        No numerology expertise needed. The system does everything automatically.
                    </p>

                    <div className="home-steps-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 0 }}>
                        {[
                            { step: '01', icon: '📝', title: 'Enter Details', desc: 'Client name, DOB, mobile number, and email' },
                            { step: '02', icon: '⚡', title: 'Auto Calculate', desc: 'Our engine instantly runs all numerological computations' },
                            { step: '03', icon: '📊', title: 'Full Report', desc: 'Grid, Yog, Dasha, Remedies — all generated in seconds' },
                            { step: '04', icon: '📤', title: 'Share & Consult', desc: 'Export PDF or share via WhatsApp with your client' },
                        ].map(({ step, icon, title, desc }, i) => (
                            <div key={i} style={{ position: 'relative', padding: '24px 20px' }}>
                                {i < 3 && (
                                    <div style={{
                                        position: 'absolute', right: 0, top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: 'rgba(206,173,128,0.2)', fontSize: 24,
                                        display: 'none',
                                    }}>→</div>
                                )}
                                <div style={{
                                    width: 60, height: 60, borderRadius: '50%', margin: '0 auto 16px',
                                    background: 'linear-gradient(135deg, rgba(206,173,128,0.2), rgba(206,173,128,0.06))',
                                    border: '2px solid rgba(206,173,128,0.3)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 26, position: 'relative',
                                }}>
                                    {icon}
                                    <div style={{
                                        position: 'absolute', top: -6, right: -6,
                                        width: 22, height: 22, borderRadius: '50%',
                                        background: '#cead80', color: '#1a0a00',
                                        fontSize: 9, fontWeight: 900,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>{step}</div>
                                </div>
                                <h3 style={{ fontSize: 17, fontWeight: 800, color: '#000000ff', marginBottom: 8 }}>{title}</h3>
                                <p style={{ fontSize: 13, color: '#64748b', lineHeight: 1.7 }}>{desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ════════════════ TESTIMONIALS ════════════════ */}
            <section style={{ position: 'relative', zIndex: 1, padding: '60px 24px 80px' }}>
                <div style={{ maxWidth: 1100, margin: '0 auto' }}>
                    <div style={{ textAlign: 'center', marginBottom: 56 }}>
                        <span style={{
                            display: 'inline-block', marginBottom: 12,
                            background: 'rgba(244,114,182,0.1)', border: '1px solid rgba(244,114,182,0.25)',
                            borderRadius: 50, padding: '5px 18px',
                            fontSize: 11, fontWeight: 700, color: '#f472b6',
                            letterSpacing: 1, textTransform: 'uppercase',
                        }}>Real Reviews</span>
                        <h2 style={{
                            fontSize: 'clamp(26px, 4vw, 44px)', fontWeight: 900, marginBottom: 12,
                            color: '#000000ff', fontFamily: 'Plus Jakarta Sans, sans-serif',
                        }}>Trusted by Numerology Professionals</h2>
                    </div>

                    <div className="home-testimonials-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24 }}>
                        {TESTIMONIALS.map((t, i) => (
                            <div key={i} style={{
                                padding: '28px 26px',
                                background: 'linear-gradient(145deg, rgba(255,255,255,0.05), rgba(255,255,255,0.01))',
                                border: '1.5px solid rgba(206,173,128,0.15)',
                                borderRadius: 20,
                                boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                            }}>
                                <div style={{ display: 'flex', marginBottom: 16 }}>
                                    {Array.from({ length: t.stars }).map((_, si) => (
                                        <span key={si} style={{ color: '#F59E0B', fontSize: 16 }}>★</span>
                                    ))}
                                </div>
                                <p style={{
                                    fontSize: 14, color: '#000000ff', lineHeight: 1.8,
                                    marginBottom: 22, fontStyle: 'italic',
                                }}>"{t.text}"</p>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <div style={{
                                        width: 40, height: 40, borderRadius: '50%',
                                        background: 'linear-gradient(135deg, #a07e4a, #cead80)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: 13, fontWeight: 800, color: '#1a0a00', flexShrink: 0,
                                    }}>{t.avatar}</div>
                                    <div>
                                        <div style={{ fontSize: 14, fontWeight: 700, color: '#000000ff' }}>{t.name}</div>
                                        <div style={{ fontSize: 12, color: '#64748b' }}>{t.role}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ════════════════ PRICING ════════════════ */}
            <section id="pricing" style={{ position: 'relative', zIndex: 1, padding: '60px 24px 80px' }}>
                <div style={{ maxWidth: 760, margin: '0 auto' }}>
                    <div style={{ textAlign: 'center', marginBottom: 48 }}>
                        <span style={{
                            display: 'inline-block', marginBottom: 12,
                            background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.25)',
                            borderRadius: 50, padding: '5px 18px',
                            fontSize: 11, fontWeight: 700, color: '#F59E0B',
                            letterSpacing: 1, textTransform: 'uppercase',
                        }}> One-Time Price</span>
                        <h2 style={{
                            fontSize: 'clamp(26px, 4vw, 44px)', fontWeight: 900, marginBottom: 12,
                            color: '#000000ff', fontFamily: 'Plus Jakarta Sans, sans-serif',
                        }}>Pay Once. Use Forever.</h2>
                        <p style={{ color: '#64748b', fontSize: 16, maxWidth: 500, margin: '0 auto' }}>
                            No subscriptions. No renewals. Get lifetime access to the complete Loshu Grid Calculator.
                        </p>
                    </div>

                    {/* Single one-time card */}
                    <div style={{
                        padding: '40px 36px',
                        background: 'linear-gradient(145deg, rgba(206,173,128,0.14), rgba(206,173,128,0.04))',
                        border: '2px solid rgba(206,173,128,0.45)',
                        borderRadius: 28,
                        position: 'relative',
                        boxShadow: '0 24px 80px rgba(206,173,128,0.18), 0 0 0 1px rgba(206,173,128,0.08)',
                    }}>
                        <div style={{
                            position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)',
                            background: 'linear-gradient(135deg, #a07e4a, #cead80)',
                            color: '#1a0a00', fontSize: 12, fontWeight: 800,
                            padding: '5px 20px', borderRadius: 50,
                            whiteSpace: 'nowrap', letterSpacing: 0.5,
                        }}>✦ LIFETIME ACCESS</div>

                        <div className="home-pricing-inner" style={{ display: 'flex', gap: 40, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                            {/* Left: features */}
                            <div style={{ flex: 1, minWidth: 240 }}>
                                <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 16 }}>Everything included:</div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    {LIFETIME_FEATURES.map((f, fi) => (
                                        <div key={fi} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                                            <div style={{
                                                width: 18, height: 18, borderRadius: '50%', flexShrink: 0, marginTop: 2,
                                                background: 'rgba(206,173,128,0.2)',
                                                border: '1.5px solid rgba(206,173,128,0.5)',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            }}>
                                                <span style={{ fontSize: 9, color: '#cead80' }}>✓</span>
                                            </div>
                                            <span style={{ fontSize: 13.5, color: '#94a3b8', lineHeight: 1.6 }}>{f}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Right: price + CTA */}
                            <div className="price-cta-col" style={{ minWidth: 220, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 16 }}>
                                <div>
                                    <div className="home-price-number" style={{ fontSize: 64, fontWeight: 900, lineHeight: 1, background: 'linear-gradient(135deg, #e8d4a8, #cead80)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>₹1,999</div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
                                        <span style={{ fontSize: 18, color: '#475569', textDecoration: 'line-through', fontWeight: 600 }}>₹11,111</span>
                                        <span style={{ background: 'rgba(52,211,153,0.15)', border: '1px solid rgba(52,211,153,0.3)', color: '#34d399', fontSize: 11, fontWeight: 800, padding: '3px 10px', borderRadius: 50 }}>Save ₹9,112</span>
                                    </div>
                                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 8 }}>♾️ One-time · Lifetime · No renewals</div>
                                </div>
                                <Link to={user ? '/dashboard' : '/register'} style={{
                                    textDecoration: 'none', display: 'block', textAlign: 'center',
                                    padding: '16px 32px', borderRadius: 50, fontSize: 15, fontWeight: 800,
                                    background: 'linear-gradient(135deg, #a07e4a, #cead80)',
                                    color: '#1a0a00',
                                    boxShadow: '0 8px 28px rgba(206,173,128,0.35)',
                                    animation: 'pulse-glow 2s ease-in-out infinite',
                                    whiteSpace: 'nowrap',
                                }}>{user ? 'Go to Dashboard →' : 'Buy Now — ₹1,999'}</Link>

                            </div>
                        </div>
                    </div>

                    <p style={{ textAlign: 'center', color: '#475569', fontSize: 13, marginTop: 24 }}>
                        Secured by Razorpay · UPI · Cards · Net Banking · Instant Access after payment
                    </p>
                </div>
            </section>

            {/* ════════════════ FAQ ════════════════ */}
            <section style={{ position: 'relative', zIndex: 1, padding: '60px 24px 80px' }}>
                <div style={{ maxWidth: 760, margin: '0 auto' }}>
                    <div style={{ textAlign: 'center', marginBottom: 48 }}>
                        <h2 style={{
                            fontSize: 'clamp(24px, 4vw, 40px)', fontWeight: 900, marginBottom: 12,
                            color: '#000000ff', fontFamily: 'Plus Jakarta Sans, sans-serif',
                        }}>Frequently Asked Questions</h2>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {FAQS.map((faq, i) => (
                            <div key={i} style={{
                                borderRadius: 16, overflow: 'hidden',
                                border: `1.5px solid ${openFaq === i ? 'rgba(206,173,128,0.4)' : 'rgba(206,173,128,0.12)'}`,
                                background: openFaq === i ? 'rgba(206,173,128,0.06)' : 'rgba(255,255,255,0.02)',
                                transition: 'all 0.3s ease',
                            }}>
                                <button className="home-faq-btn" onClick={() => setOpenFaq(openFaq === i ? null : i)} style={{
                                    width: '100%', padding: '18px 22px',
                                    background: '#ceac80', border: 'none', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
                                    textAlign: 'left',
                                }}>
                                    <span style={{ fontSize: 15, fontWeight: 700, color: '#e2e8f0', lineHeight: 1.4 }}>{faq.q}</span>
                                    <span style={{
                                        fontSize: 20, color: '#cead80', flexShrink: 0,
                                        transform: openFaq === i ? 'rotate(45deg)' : 'rotate(0)',
                                        transition: 'transform 0.3s ease',
                                    }}>+</span>
                                </button>
                                {openFaq === i && (
                                    <div style={{ padding: '0 22px 20px' }}>
                                        <p style={{ fontSize: 14, color: '#94a3b8', lineHeight: 1.8 }}>{faq.a}</p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ════════════════ BUY SECTION ════════════════ */}
            <BuySection />

            {/* ════════════════ FINAL CTA ════════════════ */}
            <section style={{ position: 'relative', zIndex: 1, padding: '60px 24px 100px' }}>
                <div style={{ maxWidth: 800, margin: '0 auto', textAlign: 'center' }}>
                    <div className="home-final-cta" style={{
                        padding: '60px 40px',
                        background: 'linear-gradient(145deg, rgba(206,173,128,0.12), rgba(206,173,128,0.03))',
                        border: '2px solid rgba(206,173,128,0.25)',
                        borderRadius: 28,
                        boxShadow: '0 20px 80px rgba(0,0,0,0.4), 0 0 0 1px rgba(206,173,128,0.08)',
                        position: 'relative', overflow: 'hidden',
                    }}>
                        <div style={{ position: 'absolute', top: -60, right: -60, width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle, rgba(206,173,128,0.15), transparent 70%)', pointerEvents: 'none' }} />
                        <div style={{ position: 'absolute', bottom: -60, left: -60, width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle, rgba(206,173,128,0.1), transparent 70%)', pointerEvents: 'none' }} />
                        <div style={{ position: 'relative' }}>
                            <div style={{ fontSize: 48, marginBottom: 16 }}>🔮</div>
                            <h2 style={{ fontSize: 'clamp(24px, 4vw, 40px)', fontWeight: 900, marginBottom: 16, background: 'linear-gradient(135deg, #e8d4a8, #cead80)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                                Ready to Transform Your Practice?
                            </h2>
                            <p style={{ color: '#94a3b8', fontSize: 16, maxWidth: 480, margin: '0 auto 36px', lineHeight: 1.8 }}>
                                Join numerology professionals who generate stunning, accurate reports in seconds.
                                One-time payment. Lifetime access.
                            </p>
                            <div className="home-final-cta-btns" style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
                                {user && isSubscribed && isSubscribed() ? (
                                    <Link to="/dashboard" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 10, background: 'linear-gradient(135deg, #a07e4a, #cead80)', color: '#1a0a00', padding: '16px 40px', borderRadius: 50, fontSize: 16, fontWeight: 800, boxShadow: '0 8px 32px rgba(206,173,128,0.4)', animation: 'pulse-glow 2s ease-in-out infinite' }}>Open Dashboard</Link>
                                ) : (
                                    <a href="#buy-section" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 10, background: 'linear-gradient(135deg, #a07e4a, #cead80)', color: '#1a0a00', padding: '16px 40px', borderRadius: 50, fontSize: 16, fontWeight: 800, boxShadow: '0 8px 32px rgba(206,173,128,0.4)', animation: 'pulse-glow 2s ease-in-out infinite' }}>Get Access — ₹1,500</a>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
